/**
 * The settings landing view: a ledger of what this school has actually
 * configured.
 *
 * Settings used to open on whichever tab happened to be first, which answered
 * a question nobody asked. An administrator opening settings in August wants to
 * know whether the school is ready, so every area reports its real contents in
 * one aligned column, and only the areas that still need something are marked.
 * Marking the finished ones too would flatten the page back into decoration.
 */
import { useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { Button } from '~/components/ui/button';
import HelpOverlay from '~/components/HelpOverlay/HelpOverlay';
import { SETTINGS_GROUPS, sectionsInGroup, settingsPath } from './settingsSections';
import { SettingsLoading } from './SettingsPanel';
import type { SettingsSummaryRow } from './settingsSummary';
import type { SettingsOutletContext } from './SettingsLayout';

const NUMBER_WORDS = ['Nothing', 'One thing', 'Two things', 'Three things', 'Four things'];

function headline(outstanding: number): string {
  if (outstanding === 0) return 'Everything here is set up.';
  const subject = NUMBER_WORDS[outstanding] ?? `${outstanding} things`;
  return `${subject} still ${outstanding === 1 ? 'needs' : 'need'} setting up.`;
}

export function SettingsOverview() {
  const { schoolSlug, schoolName, summary, loading, error } = useOutletContext<SettingsOutletContext>();
  const [helpOpen, setHelpOpen] = useState(false);
  const byslug = new Map(summary.map((row) => [row.slug, row]));
  const outstanding = summary.filter((row) => row.attention).length;

  if (loading) return <SettingsLoading />;

  if (error) {
    return (
      <p className="text-sm text-destructive">
        Settings could not be loaded: {error}. Reload the page to try again.
      </p>
    );
  }

  return (
    <div className="max-w-3xl">
      {/*
        * The guide used to live behind an icon in the app header, where it
        * followed people around every page and crowded the header on a phone.
        * It belongs where someone is already stopping to work out how the app
        * is put together.
        */}
      <header className="flex items-start justify-between gap-4 pb-8">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{headline(outstanding)}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Everything {schoolName} has configured, and everything it hasn&rsquo;t.
          </p>
        </div>
        <Button variant="outline" size="sm" className="shrink-0" onClick={() => setHelpOpen(true)}>
          How this works
        </Button>
      </header>

      <HelpOverlay isOpen={helpOpen} onClose={() => setHelpOpen(false)} />

      <div className="space-y-8">
        {SETTINGS_GROUPS.map((group) => (
          <section key={group.key} className={group.label ? undefined : 'border-t pt-8'}>
            {group.label && (
              <h2 className="text-sm font-medium text-muted-foreground">{group.label}</h2>
            )}
            <ul className={group.label ? 'mt-2' : ''}>
              {sectionsInGroup(group.key).map((section) => (
                <LedgerRow
                  key={section.slug}
                  href={settingsPath(schoolSlug, section.slug)}
                  label={section.label}
                  blurb={section.blurb}
                  row={byslug.get(section.slug)}
                />
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

interface LedgerRowProps {
  href: string;
  label: string;
  blurb: string;
  row?: SettingsSummaryRow;
}

function LedgerRow({ href, label, blurb, row }: LedgerRowProps) {
  const attention = row?.attention ?? false;

  return (
    <li className="border-t first:border-t-0">
      <Link
        to={href}
        className="group grid gap-x-6 gap-y-1 rounded-md px-3 py-3.5 -mx-3 transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:grid-cols-[minmax(0,1fr)_17rem]"
      >
        <div className="min-w-0">
          <span className="font-medium group-hover:underline">{label}</span>
          <p className="mt-0.5 text-sm text-muted-foreground">{blurb}</p>
        </div>
        <p
          className={`self-center text-sm tabular-nums ${
            attention ? 'font-medium text-[--settings-attention]' : 'text-muted-foreground'
          }`}
        >
          {attention && (
            <span
              aria-hidden
              className="mr-2 inline-block h-1.5 w-1.5 -translate-y-px rounded-full bg-[--settings-attention] align-middle"
            />
          )}
          {row?.summary}
        </p>
      </Link>
    </li>
  );
}

export default SettingsOverview;
